import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import logger from '../utils/logger';
import {
  Linkedin,
  AlertCircle,
  Database,
  CheckCircle,
  RefreshCw,
  Users,
  Target,
  BarChart3,
  Settings
} from 'lucide-react';

const LinkedinAutomationWrapper = () => {
  const [systemStatus, setSystemStatus] = useState('checking');
  const [missingTables, setMissingTables] = useState([]);
  const [error, setError] = useState(null);
  const [LinkedinAutomationControl, setLinkedinAutomationControl] = useState(null);

  // Required tables for LinkedIn automation
  const requiredTables = [
    'linkedin_accounts',
    'linkedin_campaigns',
    'linkedin_connections',
    'linkedin_messages',
    'linkedin_automation_sequences',
    'multichannel_campaigns',
    'lead_channel_activities',
    'lead_reply_detection'
  ];

  useEffect(() => {
    checkSystemRequirements();
  }, []);

  const checkSystemRequirements = async () => {
    try {
      setSystemStatus('checking');
      setError(null);

      // Check if required tables exist
      const missing = [];
      
      for (const tableName of requiredTables) {
        try {
          const { error } = await supabase
            .from(tableName)
            .select('id')
            .limit(1);
          
          if (error && error.code === '42P01') { // Table does not exist
            missing.push(tableName);
          }
        } catch (err) {
          if (err.code === '42P01') {
            missing.push(tableName);
          }
        }
      }

      setMissingTables(missing);

      if (missing.length === 0) {
        // All tables exist, load the component
        setSystemStatus('ready');
        loadLinkedinAutomationControl();
      } else {
        setSystemStatus('missing_tables');
      }

    } catch (error) {
      logger.error('Error checking system requirements:', error);
      setError('Failed to check system requirements');
      setSystemStatus('error');
    }
  };

  const loadLinkedinAutomationControl = async () => {
    try {
      // Dynamically import the LinkedinAutomationControl component
      const { default: Component } = await import('./linkedinautomationcontrol');
      setLinkedinAutomationControl(() => Component);
    } catch (error) {
      logger.error('Error loading LinkedinAutomationControl:', error);
      setError('Failed to load LinkedIn Automation component');
      setSystemStatus('error');
    }
  };

  const renderSystemStatus = () => {
    switch (systemStatus) {
      case 'checking':
        return (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Checking LinkedIn automation system...</p>
            </div>
          </div>
        );

      case 'missing_tables':
        return (
          <div className="max-w-4xl mx-auto p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <Linkedin className="w-6 h-6 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">
                    LinkedIn Automation System Not Installed
                  </h3>
                  <p className="text-blue-700 mb-4">
                    The LinkedIn automation system requires additional database tables to function. 
                    The following tables are missing:
                  </p>
                  
                  <div className="bg-blue-100 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-blue-800 mb-2">Missing Tables:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {missingTables.map((table) => (
                        <li key={table} className="text-blue-700 text-sm font-mono">
                          {table}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <Database className="w-5 h-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-green-800 mb-2">Installation Instructions:</h4>
                        <ol className="list-decimal list-inside space-y-2 text-green-700 text-sm">
                          <li>Run the Universal Lead System schema file on your database</li>
                          <li>Execute: <code className="bg-green-100 px-2 py-1 rounded text-xs">universal-lead-system-schema-safe.sql</code></li>
                          <li>This will install both Lead List Management and LinkedIn Automation</li>
                          <li>Refresh this page after installation</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={checkSystemRequirements}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span>Check Again</span>
                    </button>
                    <span className="text-blue-600 text-sm">
                      Click "Check Again" after running the schema file
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Preview */}
            <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                LinkedIn Automation Features:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <Users className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Multi-Account Management</h4>
                    <p className="text-gray-600 text-sm">Manage unlimited LinkedIn accounts with safety limits and rotation</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Target className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Automated Campaigns</h4>
                    <p className="text-gray-600 text-sm">Connection requests, message sequences, and engagement campaigns</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <BarChart3 className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Advanced Analytics</h4>
                    <p className="text-gray-600 text-sm">Real-time performance tracking and multichannel attribution</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-orange-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Cross-Channel Integration</h4>
                    <p className="text-gray-600 text-sm">Automatic coordination with email campaigns and reply detection</p>
                  </div>
                </div>
              </div>
            </div>

            {/* HeyReach Integration Info */}
            <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start">
                <Settings className="w-5 h-5 text-purple-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-purple-800 mb-1">HeyReach Integration Ready</h4>
                  <p className="text-purple-700 text-sm">
                    The system is designed to integrate with HeyReach for LinkedIn automation. 
                    You'll need to configure your HeyReach API key after installation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="max-w-2xl mx-auto p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-red-800 mb-2">System Error</h3>
                  <p className="text-red-700 mb-4">{error}</p>
                  <button
                    onClick={checkSystemRequirements}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Retry</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'ready':
        if (LinkedinAutomationControl) {
          return <LinkedinAutomationControl />;
        } else {
          return (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading LinkedIn Automation...</p>
              </div>
            </div>
          );
        }

      default:
        return (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Unknown system state</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">LinkedIn Automation</h2>
          <p className="text-gray-600">Manage LinkedIn campaigns and multichannel outreach</p>
        </div>
        {systemStatus === 'ready' && (
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">System Ready</span>
          </div>
        )}
      </div>

      {/* System Status Content */}
      {renderSystemStatus()}
    </div>
  );
};

export default LinkedinAutomationWrapper;